import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { withTransparentErrorBoundary, Attribution } from '@confluence/error-boundary';

import { UserTypes, ANALYTICS_SOURCE } from '../constants';

import { JoinButton } from './JoinButton';
import { RedirectButton } from './RedirectButton';
import { TryButton } from './TryButton';
import { LogInButton } from './LogInButton';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.span({
	display: 'flex',
	gap: token('space.100', '8px'),
});

const ConditionalUserButtonsComponent: FC<{
	userType: UserTypes;
	contentId: string;
	cloudId: string;
	spaceKey?: string | null;
	renderLocation: string;
	reverseButtonOrder?: boolean;
	shortenTryButton?: boolean;
	contentType?: string | null;
}> = ({
	userType,
	contentId,
	cloudId,
	spaceKey,
	renderLocation,
	reverseButtonOrder,
	shortenTryButton,
	contentType,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendShownAnalyticsEvent = (actionSubjectId: string, location: string) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'shown',
				actionSubject: 'button',
				actionSubjectId,
				source: ANALYTICS_SOURCE,
				attributes: {
					location,
					contentType,
				},
			},
		}).fire();
	};

	if (userType === UserTypes.CONFLUENCE_USER) {
		sendShownAnalyticsEvent('openInConfluenceButton', renderLocation || 'banner');
		return (
			<RedirectButton
				contentId={contentId}
				spaceKey={spaceKey}
				renderLocation={renderLocation}
				contentType={contentType}
			/>
		);
	}
	if (userType === UserTypes.ORG_MEMBER) {
		sendShownAnalyticsEvent('joinTeamButton', renderLocation);
		return (
			<JoinButton
				contentId={contentId}
				cloudId={cloudId}
				spaceKey={spaceKey}
				renderLocation={renderLocation}
				contentType={contentType}
			/>
		);
	}
	if (userType === UserTypes.ANONYMOUS) {
		sendShownAnalyticsEvent('logInToConfluenceButton', renderLocation);

		sendShownAnalyticsEvent('tryConfluenceButton', renderLocation);

		return (
			<ButtonWrapper>
				{/* This is actually a really bad prop name, this is a different style presentation
        depending on which component rendering this component .. we never want the
        "Make a Copy" button to show on the other screens :/  */}
				{reverseButtonOrder ? (
					<>
						<TryButton
							contentId={contentId}
							renderLocation={renderLocation}
							shortened={shortenTryButton}
							contentType={contentType}
						/>
						<LogInButton
							contentId={contentId}
							renderLocation={renderLocation}
							contentType={contentType}
						/>
					</>
				) : (
					<>
						<LogInButton
							contentId={contentId}
							renderLocation={renderLocation}
							contentType={contentType}
						/>
						<TryButton
							contentId={contentId}
							renderLocation={renderLocation}
							shortened={shortenTryButton}
							contentType={contentType}
						/>
					</>
				)}
			</ButtonWrapper>
		);
	} else {
		sendShownAnalyticsEvent('tryConfluenceButton', renderLocation);

		return (
			<ButtonWrapper>
				<TryButton
					contentId={contentId}
					renderLocation={renderLocation}
					shortened={shortenTryButton}
					contentType={contentType}
				/>
			</ButtonWrapper>
		);
	}
};

export const ConditionalUserButtons = withTransparentErrorBoundary({
	attribution: Attribution.EXTERNAL_COLLAB,
})(ConditionalUserButtonsComponent);
